import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { effectiveCapitalApi } from '../../helpers/effectiveCapitalApi';
import { formatCurrencyFull } from '../../helpers/effectiveCapitalFormat';
import { LoaderSpinner } from '../LoaderSpinner';

const DATE_COLUMN_WIDTH = 'w-32';
const TIMESTAMP_COLUMN_WIDTH = 'w-28';

const money = (value) => (value == null ? '' : formatCurrencyFull(value));

const dateLabel = (value) => new Date(`${value}T12:00:00`).toLocaleDateString('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const classificationLabel = {
  cash: 'Cash',
  receivables: 'Receivables',
  cc: 'CC Liabilities',
};

export const EffectiveCapitalSummaryHistory = ({
  entity,
  onLoadingChange,
  viewSwitcher,
}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const requestRef = useRef({ id: 0, controller: null });
  const previousEntityRef = useRef(entity);

  const loadData = useCallback(async (params, requestId) => {
    const controller = new AbortController();
    requestRef.current.controller = controller;
    setIsLoading(true);
    setError(null);
    try {
      const nextData = await effectiveCapitalApi.getEcHistory(params, {
        signal: controller.signal,
      });
      if (requestRef.current.id === requestId) {
        setData(nextData);
      }
    } catch (loadError) {
      const isCurrentRequest = requestRef.current.id === requestId;
      if (isCurrentRequest && !controller.signal.aborted) {
        setError(loadError);
        toast.error(loadError?.response?.data?.error || 'Failed to load history', {
          position: 'bottom-right',
        });
      }
    } finally {
      if (requestRef.current.id === requestId) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const requestState = requestRef.current;
    const requestId = requestState.id + 1;
    requestState.id = requestId;
    requestState.controller?.abort();
    const entityChanged = previousEntityRef.current !== entity;
    previousEntityRef.current = entity;
    const params = {
      entity,
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
    };
    const timeout = setTimeout(() => {
      loadData(params, requestId);
    }, entityChanged ? 0 : 400);

    return () => {
      clearTimeout(timeout);
      if (requestState.id === requestId) {
        requestState.controller?.abort();
      }
    };
  }, [entity, startDate, endDate, loadData]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const exportCSV = () => {
    if (!data?.rows?.length) return;

    const headers = [
      'Date',
      'Timestamp',
      ...data.columns.map((column) => column.label),
      'Cash',
      'Receivables',
      'CC Liabilities',
      'Effective Capital',
    ];
    const rows = data.rows.map((row) => [
      row.date,
      row.timestamp || '',
      ...data.columns.map((column) => row.values[column.key] ?? ''),
      row.totals.cash,
      row.totals.receivables,
      row.totals.ccLiabilities,
      row.totals.effectiveCapital,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${entity}-effective-capital-history.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const classificationGroups = useMemo(() => {
    if (!data?.columns?.length) return [];
    return data.columns.reduce((groups, column) => {
      const label = classificationLabel[column.classification] || column.classification;
      const last = groups[groups.length - 1];
      if (last?.label === label) {
        last.span += 1;
      } else {
        groups.push({ label, span: 1 });
      }
      return groups;
    }, []);
  }, [data]);

  const header = (
    <div className="flex shrink-0 flex-col gap-3 border-b border-gray-200 py-3 mb-4">
      <div>
        {viewSwitcher || (
          <h2 className="text-sm font-bold uppercase tracking-widest">
            Effective Capital History
          </h2>
        )}
        <p className="text-xs text-gray-500">
          Workbook history and subsequent effective capital snapshots
        </p>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
        <label className="flex items-center gap-2 text-sm">
          From
          <input
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          To
          <input
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={exportCSV}
          disabled={!data?.rows?.length || isLoading}
          className="sm:col-span-2 lg:col-span-1 flex items-center justify-center gap-1.5 border bg-primary px-4 rounded-lg py-2.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <ArrowDownTrayIcon className="w-5 h-5 shrink-0" />
          Export CSV
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col">
      {header}
      {isLoading ? (
        <div className="flex h-32 items-center justify-center text-sm text-gray-500 mt-10">
          <LoaderSpinner height="80" width="80" colorSpinner="black" border="4" />
        </div>
      ) : error ? (
        <div className="flex h-32 items-center justify-center text-sm text-gray-500">
          Unable to load effective capital history.
        </div>
      ) : !data?.rows?.length ? (
        <div className="flex h-32 items-center justify-center text-sm text-gray-500">
          No history found for this date range.
        </div>
      ) : (
        <>
          <div className="mb-3 text-sm text-gray-500">
            {data.rows.length} rows · {dateLabel(data.rows.at(-1).date)} – {dateLabel(data.rows[0].date)}
          </div>
          <div className="overflow-x-auto">
            <table className="eadin-table eadin-table--sticky-cols min-w-max">
              <thead>
                <tr>
                  <th
                    colSpan="2"
                    className="sticky left-0 z-20 w-60 bg-[oklch(0.99_0_0)]"
                  >
                    CLASSIFICATION
                  </th>
                  {classificationGroups.map((group, index) => (
                    <th key={`classification-${group.label}-${index}`} colSpan={group.span} className="text-center">
                      {group.label}
                    </th>
                  ))}
                  <th key="derived-totals" colSpan="4" className="text-center">DERIVED TOTALS</th>
                </tr>
                <tr>
                  <th
                    colSpan="2"
                    className="sticky left-0 z-20 w-60 bg-[oklch(0.99_0_0)]"
                  >
                    SIGN
                  </th>
                  {data.columns.map((column) => (
                    <th key={column.key} className="text-center">
                      {column.sign}
                    </th>
                  ))}
                  <th colSpan="4" />
                </tr>
                <tr>
                  <th
                    colSpan="2"
                    className="sticky left-0 z-20 w-60 bg-[oklch(0.99_0_0)]"
                  >
                    SOURCE
                  </th>
                  {data.columns.map((column) => (
                    <th key={column.key} className="text-center">
                      {column.source}
                    </th>
                  ))}
                  <th colSpan="4" />
                </tr>
                <tr>
                  <th className={`sticky left-0 z-20 bg-[oklch(0.99_0_0)] ${DATE_COLUMN_WIDTH}`}>
                    Date
                  </th>
                  <th className={`sticky left-32 z-20 bg-[oklch(0.99_0_0)] ${TIMESTAMP_COLUMN_WIDTH}`}>
                    Timestamp
                  </th>
                  {data.columns.map((column) => (
                    <th key={column.key} className="text-center">
                      {column.label}
                    </th>
                  ))}
                  <th key="derived-cash" className="border-l-2 text-center">Cash</th>
                  <th key="derived-receivables" className="text-center">Receivables</th>
                  <th key="derived-cc-liabilities" className="text-center">CC Liabilities</th>
                  <th key="derived-effective-capital" className="text-center">Effective Capital</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.date}>
                    <td className={`sticky left-0 z-10 ${DATE_COLUMN_WIDTH} bg-inherit font-medium`}>
                      <span className="inline-flex items-center gap-1.5">
                        {dateLabel(row.date)}
                        {row.source === 'snapshot' && (
                          <span
                            title="Captured automatically from an effective capital snapshot"
                            className="rounded bg-gray-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-gray-600"
                          >
                            auto
                          </span>
                        )}
                      </span>
                    </td>
                    <td className={`sticky left-32 z-10 ${TIMESTAMP_COLUMN_WIDTH} bg-inherit text-xs`}>
                      {row.timestamp || '—'}
                    </td>
                    {data.columns.map((column) => (
                      <td key={column.key} className={`text-center tabular-nums ${row.values[column.key] < 0 ? 'value-negative' : ''}`}>
                        {money(row.values[column.key])}
                      </td>
                    ))}
                    <td className={`border-l-2 text-center tabular-nums ${row.totals.cash < 0 ? 'value-negative' : ''}`}>
                      {money(row.totals.cash)}
                    </td>
                    <td className={`text-center tabular-nums ${row.totals.receivables < 0 ? 'value-negative' : ''}`}>
                      {money(row.totals.receivables)}
                    </td>
                    <td className={`text-center tabular-nums ${row.totals.ccLiabilities < 0 ? 'value-negative' : ''}`}>
                      {money(row.totals.ccLiabilities)}
                    </td>
                    <td className={`text-center font-semibold tabular-nums ${row.totals.effectiveCapital < 0 ? 'value-negative' : ''}`}>
                      {money(row.totals.effectiveCapital)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
